import React, { Fragment, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import {
  sendSignupOtp,
  signUp,
  updateSignUpData,
} from "../../../store/reducers/signUpSlice";
import { loginUser } from "../../../store/reducers/authSlice";
import * as Yup from "yup";
import logo from "../../../assets/media/logo.png";
import GamingBoardBahamasLogo from "../../../assets/media/GamingBoardBahamasLogo.png";
import GBInput from "../../input";
import { showError } from "../../../helpers/toast";

const SignUp2 = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const signUpUser = useSelector((state) => state.signUp);
  const user = useSelector((state) => state.auth.user);
  const loading = useSelector((state) => state.signUp.loading);
  const signupData = useSelector((state) => state?.signUp?.data?.payload);
  const [isInvalidSignupCode, setIsInvalidSignupCode] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const initialValues = {
    phone: signUpUser?.phone || user?.phone || "",
    companyRegistrationNo: user?.company?.company_id || "",
  };

  const validationSchema = Yup.object({
    phone: Yup.string().required("Phone Number is required"),
    companyRegistrationNo: Yup.string(),
  });

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setFieldError }) => {
      if (!signUpUser?.signupcode) {
        showError(
          "Signup session expired. Please use your invitation link again."
        );
        navigate("/");
        return;
      }

      const updatedData = {
        signupcode: signUpUser.signupcode,
        password: signUpUser.password,
        full_name: signUpUser.full_name,
        phone: values.phone,
        companyRegistrationNo: values.companyRegistrationNo,
      };
      dispatch(updateSignUpData(updatedData));

      setSubmitting(true);
      try {
        const otpResult = await dispatch(
          sendSignupOtp({
            signupcode: signUpUser.signupcode,
            phone: values.phone,
          })
        ).unwrap();

        // MFA off / exempt — submit signup immediately (no OTP screen)
        if (!otpResult?.otpRequired) {
          const signupResponse = await dispatch(
            signUp({
              userData: {
                signupcode: signUpUser.signupcode,
                password: signUpUser.password,
                full_name: signUpUser.full_name,
                phone: values.phone,
                companyRegistrationNo: values.companyRegistrationNo,
              },
            })
          ).unwrap();

          const userEmail =
            signupResponse?.user?.email || user?.email;
          if (userEmail && signUpUser.password) {
            await dispatch(
              loginUser({
                emailOrPhone: userEmail?.toLowerCase()?.trim(),
                password: signUpUser.password,
              })
            );
          } else {
            navigate("/");
          }
          return;
        }

        navigate("/signup-step3");
      } catch (err) {
        if (err?.invalidSignupCode) {
          setIsInvalidSignupCode(true);
          return;
        }
        if (err?.invalidPhone) {
          setFieldError(
            "phone",
            err.message ||
              "Enter a valid mobile number that can receive SMS."
          );
          return;
        }
        showError(
          err?.message ||
            "Phone verification is unavailable. Please try again later."
        );
      } finally {
        setSubmitting(false);
      }
    },
  });
  const { values, handleChange, handleSubmit, errors } = formik;

  const fields = [
    {
      name: "phone",
      label: "Phone Number",
      type: "tel",
      placeholder: "(702) 555-0122",
      value: values.phone,
      onChange: handleChange,
      readOnly: false,
    },
    ...(user?.user_role === "company_admin"
      ? [
          {
            name: "companyRegistrationNo",
            label: "Company Registration No.",
            type: "text",
            placeholder: "Enter Company Registration No.",
            value: values.companyRegistrationNo,
            readOnly: user?.company?.company_id ? true : false,
            onChange: handleChange,
          },
        ]
      : []),
  ];

  useEffect(() => {
    if (!signUpUser?.signupcode || !signUpUser?.password) {
      navigate("/");
    }
  }, [navigate, signUpUser?.signupcode, signUpUser?.password]);

  useEffect(() => {
    if (user?.access_token || signupData?.access_token) {
      navigate("/");
    }
  }, [navigate, user, signupData]);

  const busy = loading || submitting;

  return (
    <div className="App">
      <div className="container">
        <div className="landingWrapper">
          <div className="landingRow">
            <div className="landingCol">
              <div className="fieldsWrapper">
                <img src={logo} className="logo" alt="logo" />
                {isInvalidSignupCode ? (
                  <>
                    <h2>Invitation link expired</h2>
                    <p>
                      This signup link is no longer valid. Please ask your
                      administrator for a new invite.
                    </p>
                    <div className="gb-form-footer">
                      <Link to="/" className="form-submit">
                        Back to Sign In
                      </Link>
                    </div>
                  </>
                ) : (
                  <>
                    <h2>Sign Up With Us</h2>
                    <p>
                      Welcome to the Gaming Board of The Bahamas Digital
                      Application Portal.
                    </p>
                    <form className="gb-login-form" onSubmit={handleSubmit}>
                      {fields.map((field, index) => {
                        return (
                          <Fragment key={index}>
                            <GBInput
                              name={field.name}
                              type={field.type}
                              label={field.label}
                              value={field.value}
                              placeholder={field.placeholder}
                              setValue={handleChange}
                              readOnly={field.readOnly}
                            />
                            {errors[`${field.name}`] && (
                              <div className="error-message error-message-wrap">
                                {errors[`${field.name}`]}
                              </div>
                            )}
                          </Fragment>
                        );
                      })}
                      <div className="gb-form-footer">
                        <button
                          type="submit"
                          className="form-submit"
                          disabled={busy}
                        >
                          {busy ? (
                            <>
                              <div
                                className="text-xs spinner-border text-light"
                                style={{ width: "1rem", height: "1rem" }}
                                role="status"
                              ></div>
                              <span className="sr-only ml-5"> Loading...</span>
                            </>
                          ) : (
                            "Continue"
                          )}
                        </button>
                      </div>
                    </form>
                    <div className="gb-step-list">
                      <span className="current"></span>
                      <span className="current"></span>
                      <span></span>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="landingCol">
              <div className="imageWrapper">
                <div className="loginBox">
                  <p>
                    Already have account? <Link to={"/"}>Sign In</Link>
                  </p>
                </div>
                <img
                  width={"300px"}
                  height={"300px"}
                  src={GamingBoardBahamasLogo}
                  className="booksAnimation"
                  alt=""
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp2;
